import React, { useState, useMemo } from "react";
import { zoomDragStyle } from "../../lib/pointerDrop";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { X } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { PlayerAvatar } from "../../components/PlayerAvatar";
import { inputCls } from "./shared";

export const RESERVE_LABELS = ["Scratch", "Reserve", "IR", "LTIR", "Suspended"];

const FW_SLOT_LABELS = ["LW", "C", "RW"];
const D_SLOT_LABELS = ["LD", "RD"];
const G_SLOT_LABELS = ["Starter", "Backup"];

// Special Teams slot labels
const PP_SLOT_LABELS = ["F", "F", "F", "D", "D"];     // 5 skaters
const PK_SLOT_LABELS = ["F", "F", "D", "D"];           // 4 skaters

export function emptyLineup() {
    return {
        coach_id: "",
        forward_lines: Array.from({ length: 5 }, () => Array.from({ length: 3 }, () => ({ player_id: "", player_name: "" }))),
        d_pairs: Array.from({ length: 4 }, () => Array.from({ length: 2 }, () => ({ player_id: "", player_name: "" }))),
        goalies: Array.from({ length: 2 }, () => ({ player_id: "", player_name: "" })),
        reserves: [],
        pp1: Array.from({ length: 5 }, () => ({ player_id: "", player_name: "" })),
        pp2: Array.from({ length: 5 }, () => ({ player_id: "", player_name: "" })),
        pk1: Array.from({ length: 4 }, () => ({ player_id: "", player_name: "" })),
        pk2: Array.from({ length: 4 }, () => ({ player_id: "", player_name: "" })),
        extra_forwards: [],
        extra_defenders: [],
        reserve_goalies: [],
        injured_reserve: [],
        special_units: {},
        shootout_order: [],
    };
}

// Normalise any stored / server lineup into a fully-padded editable draft.
// Null slots (left behind when a transferred player is cleaned out) are
// converted to empty slots so they neither crash the builder nor reappear.
export function hydrateLineup(src) {
    src = src || {};
    const empty = emptyLineup();
    const ES = () => ({ player_id: "", player_name: "" });
    const slot = (s) => (s && typeof s === "object" ? s : ES());
    const line = (l, n) => {
        const out = (l || []).slice(0, n).map(slot);
        while (out.length < n) out.push(ES());
        return out;
    };
    const padFixed = (arr, n) => line(arr, n);
    const flat = (arr) => (arr || []).filter((s) => s && typeof s === "object");
    const fl = (src.forward_lines || []).slice(0, 5).map((l) => line(l, 3));
    while (fl.length < 5) fl.push(line([], 3));
    const dp = (src.d_pairs || []).slice(0, 4).map((l) => line(l, 2));
    while (dp.length < 4) dp.push(line([], 2));
    return {
        coach_id: src.coach_id || "",
        forward_lines: fl,
        d_pairs: dp,
        goalies: src.goalies ? padFixed(src.goalies, Math.max(2, src.goalies.length)) : empty.goalies,
        reserves: flat(src.reserves),
        pp1: padFixed(src.pp1, 5),
        pp2: padFixed(src.pp2, 5),
        pk1: padFixed(src.pk1, 4),
        pk2: padFixed(src.pk2, 4),
        extra_forwards: flat(src.extra_forwards),
        extra_defenders: flat(src.extra_defenders),
        reserve_goalies: flat(src.reserve_goalies),
        injured_reserve: flat(src.injured_reserve),
        accents: (src.accents && typeof src.accents === "object") ? src.accents : {},
        special_units: (src.special_units && typeof src.special_units === "object") ? src.special_units : {},
        shootout_order: Array.isArray(src.shootout_order) ? src.shootout_order : [],
        layout: (src.layout && typeof src.layout === "object") ? src.layout : {},
    };
}

// =================== Drag-and-drop lineup builder ===================
const ST_TYPES = new Set(["pp1", "pp2", "pk1", "pk2"]);
const EMPTY_SLOT = { player_id: "", player_name: "" };
const keyType = (k) => k.split("-")[0];
const posRank = (p) => {
    const s = (p?.position || "").toUpperCase();
    if (s.startsWith("G")) return 3;
    if (s === "D" || /D$/.test(s)) return 2;
    return 1;
};
const posTag = (p) => {
    const s = (p?.position || "").toUpperCase();
    if (s.startsWith("G")) return "G";
    if (s === "D" || /D$/.test(s)) return "D";
    return "F";
};

function getSlot(d, key) {
    const [t, a, b] = key.split("-");
    if (t === "fw") return d.forward_lines[+a][+b];
    if (t === "dp") return d.d_pairs[+a][+b];
    if (t === "g") return d.goalies[+a];
    if (t === "res") return d.reserves[+a] || EMPTY_SLOT;
    return d[t][+a]; // pp1 / pp2 / pk1 / pk2
}
function writeSlot(d, key, val) {
    const [t, a, b] = key.split("-");
    if (t === "fw") d.forward_lines[+a][+b] = val;
    else if (t === "dp") d.d_pairs[+a][+b] = val;
    else if (t === "g") d.goalies[+a] = val;
    else d[t][+a] = val; // pp/pk
    return d;
}

// Presentational player chip. `dragHandleProps` come from a @hello-pangea/dnd
// Draggable; the chip itself is the drag handle.
function PlayerChip({ name, photo, badge, dragHandleProps, onRemove, testId, dragging, onContextMenu, onHoverEnd, danger, photoX, photoY, photoZoom }) {
    return (
        <div
            {...(dragHandleProps || {})}
            onContextMenu={onContextMenu}
            onMouseEnter={onContextMenu}
            onMouseLeave={onHoverEnd}
            className={`relative flex flex-col items-center select-none cursor-grab active:cursor-grabbing ${dragging ? "opacity-90" : ""}`}
            data-testid={testId}
        >
            <div className="relative">
                <PlayerAvatar photo={photo} name={name} size="lg" photoX={photoX} photoY={photoY} photoZoom={photoZoom} />
                {badge && (
                    <span className="absolute -top-1 -right-1 bg-[#0A0B10] border border-white/15 rounded-full px-1 py-0.5 text-[8px] uppercase font-bold tracking-wider text-white/70">{badge}</span>
                )}
            </div>
            <span className={`text-[10px] font-bold mt-1 leading-tight text-center max-w-[74px] truncate ${danger ? "text-[#F87171]" : "text-white"}`} title={danger ? `${name} — out of position` : name}>{name}</span>
            {onRemove && (
                <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={onRemove}
                    className="absolute -top-1 -left-1 bg-[#0A0B10] border border-white/20 rounded-full p-0.5 text-[#FCA5A5] hover:text-[#EF4444]"
                    title="Remove"
                >
                    <X className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}

// A single-occupancy slot: it's a Droppable holding at most one Draggable.
function SlotCell({ slotKey, label, val, byId, onClear, onInfo }) {
    const pid = val?.player_id;
    const p = pid ? byId[pid] : null;
    // Slot category from the key: g- → G, dp- → D, everything else → skater (F).
    const cat = slotKey.startsWith("g-") ? "G" : slotKey.startsWith("dp-") ? "D" : "F";
    const playerIsG = (p?.position || "").toUpperCase().startsWith("G");
    // "Totally out of position" = goalie in a skater slot, or skater in goal.
    const danger = !!pid && ((playerIsG && cat !== "G") || (!playerIsG && cat === "G"));
    return (
        <Droppable droppableId={`slot:${slotKey}`}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`relative rounded-md min-h-[80px] flex items-center justify-center p-1 border transition-colors ${snapshot.isDraggingOver ? "border-[#9D4CDD] bg-[#9D4CDD]/15" : pid ? "border-[#1E2233] bg-black/20" : "border-dashed border-[#2A2F45]"}`}
                    data-testid={`lineup-slot-${slotKey}`}
                >
                    {pid ? (
                        <Draggable draggableId={`slot:${slotKey}`} index={0}>
                            {(prov, snap) => (
                                <div ref={prov.innerRef} {...prov.draggableProps} style={zoomDragStyle(prov.draggableProps.style)}>
                                    <PlayerChip
                                        dragHandleProps={prov.dragHandleProps}
                                        name={val.player_name || p?.name || pid}
                                        photo={p?.photo_url}
                                        photoX={p?.photo_x} photoY={p?.photo_y} photoZoom={p?.photo_zoom}
                                        badge={label}
                                        danger={danger}
                                        onRemove={onClear}
                                        onContextMenu={onInfo ? onInfo(pid) : undefined}
                                        onHoverEnd={onInfo ? onInfo(null) : undefined}
                                        dragging={snap.isDragging}
                                        testId={`slot-token-${slotKey}`}
                                    />
                                </div>
                            )}
                        </Draggable>
                    ) : (
                        <span className="h-12 w-12 rounded-full border border-dashed border-[#2A2F45] flex items-center justify-center text-[10px] text-[#3A3F55] font-bold uppercase tracking-wider">{label}</span>
                    )}
                    {/* Keep placeholder out of layout so fixed slots don't jump. */}
                    <div style={{ display: "none" }}>{provided.placeholder}</div>
                </div>
            )}
        </Droppable>
    );
}

const BuilderColumn = ({ title, primary, children, testId }) => (
    <div className="rosterly-card p-3 space-y-2" style={{ borderTop: `3px solid ${primary}` }} data-testid={testId}>
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD] mb-1 border-b border-[#1E2233] pb-1">{title}</p>
        {children}
    </div>
);
const BuilderBlock = ({ label, children }) => (
    <div className="bg-black/20 rounded-md p-2">
        <p className="text-[9px] uppercase tracking-wider font-bold text-[#64748B] mb-2">{label}</p>
        {children}
    </div>
);

function ReservesColumn({ reserves, byId, onClear, onLabelChange, reserveLabels, onInfo }) {
    return (
        <Droppable droppableId="reserves">
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-2 min-h-[80px] rounded-md p-1 transition-colors ${snapshot.isDraggingOver ? "bg-[#9D4CDD]/10 ring-1 ring-[#9D4CDD]" : ""}`}
                    data-testid="lineup-reserves-drop"
                >
                    {(reserves || []).length === 0 && !snapshot.isDraggingOver ? (
                        <p className="text-[11px] text-[#64748B] italic text-center py-4">Drop players here</p>
                    ) : (
                        (reserves || []).map((r, i) => (
                            <Draggable key={`res-${i}-${r.player_id}`} draggableId={`res:${i}`} index={i}>
                                {(prov) => (
                                    <div
                                        ref={prov.innerRef}
                                        {...prov.draggableProps} style={zoomDragStyle(prov.draggableProps.style)}
                                        className="flex items-center gap-2 bg-black/20 rounded-md p-2"
                                        data-testid={`reserve-chip-${i}`}
                                    >
                                        <PlayerChip
                                            dragHandleProps={prov.dragHandleProps}
                                            name={r.player_name || r.player_id}
                                            photo={byId[r.player_id]?.photo_url}
                                            photoX={byId[r.player_id]?.photo_x} photoY={byId[r.player_id]?.photo_y} photoZoom={byId[r.player_id]?.photo_zoom}
                                            onRemove={() => onClear(`res-${i}`)}
                                            onContextMenu={onInfo ? onInfo(r.player_id) : undefined}
                                            onHoverEnd={onInfo ? onInfo(null) : undefined}
                                            testId={`reserve-token-${i}`}
                                        />
                                        <select
                                            value={r.label || "Scratch"}
                                            onChange={(e) => onLabelChange(i, e.target.value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className={inputCls + " text-[11px] py-1 flex-1"}
                                            data-testid={`reserve-label-${i}`}
                                        >
                                            {reserveLabels.map((l) => <option key={l} value={l}>{l}</option>)}
                                        </select>
                                    </div>
                                )}
                            </Draggable>
                        ))
                    )}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
}

// Effective bucket for the pool: a player's designated position, else their
// PREFERRED position (so "undesignated" players are segmented under the
// position they prefer), else "U" (truly undesignated).
const POOL_GROUPS = [["F", "Forwards"], ["D", "Defense"], ["G", "Goalies"], ["U", "Undesignated"]];
const poolEffPos = (p) => {
    const s = (p?.position || p?.pref_position || "").toUpperCase();
    if (s.startsWith("G")) return "G";
    if (s === "D" || /D$/.test(s)) return "D";
    if (s) return "F";
    return "U";
};

function PoolBox({ players, query, setQuery, onInfo, droppableId = "pool", title = "Available Players", testId = "lineup-pool-box", emptyHint = "All players placed — drag one back here to free it up." }) {
    const groups = useMemo(() => {
        const g = { F: [], D: [], G: [], U: [] };
        players.forEach((p) => { g[poolEffPos(p)].push(p); });
        return g;
    }, [players]);
    let idx = 0;
    return (
        <div className="rosterly-card p-4 space-y-3" data-testid={testId}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-[#E9D5FF]">{title} <span className="text-[#64748B] ml-1">({players.length})</span></h4>
                {setQuery && <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className={inputCls + " text-xs py-1.5 w-[160px]"} data-testid={`${testId}-search`} />}
            </div>
            <Droppable droppableId={droppableId} direction="horizontal">
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`min-h-[96px] rounded-md p-2 flex flex-col gap-3 transition-colors ${snapshot.isDraggingOver ? "bg-[#9D4CDD]/10 ring-1 ring-[#9D4CDD]" : "bg-black/20"}`}
                    >
                        {players.length === 0 && !snapshot.isDraggingOver ? (
                            <p className="text-xs text-[#64748B] italic text-center py-6 w-full">{emptyHint}</p>
                        ) : (
                            POOL_GROUPS.map(([key, label]) => groups[key].length > 0 && (
                                <div key={key} className="w-full" data-testid={`pool-group-${key}`}>
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1.5">
                                        {label} <span className="text-[#475569]">({groups[key].length})</span>
                                    </div>
                                    <div className="flex flex-wrap gap-3 content-start">
                                        {groups[key].map((p) => {
                                            const i = idx++;
                                            return (
                                                <Draggable key={p.id} draggableId={`pool:${p.id}`} index={i}>
                                                    {(prov, snap) => (
                                                        <div ref={prov.innerRef} {...prov.draggableProps} style={zoomDragStyle(prov.draggableProps.style)}>
                                                            <PlayerChip
                                                                dragHandleProps={prov.dragHandleProps}
                                                                name={p.name}
                                                                photo={p.photo_url}
                                                                photoX={p.photo_x} photoY={p.photo_y} photoZoom={p.photo_zoom}
                                                                badge={posTag(p)}
                                                                dragging={snap.isDragging}
                                                                onContextMenu={onInfo ? onInfo(p.id) : undefined}
                                                                onHoverEnd={onInfo ? onInfo(null) : undefined}
                                                                testId={`pool-token-${p.id}`}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                        {/* Hidden: we never need ordered insertion in the pool. */}
                        <div style={{ display: "none" }}>{provided.placeholder}</div>
                    </div>
                )}
            </Droppable>
        </div>
    );
}

export function LineupBuilder({ draft, setDraft, pool, players, primary, reserveLabels = RESERVE_LABELS, extraPool = [] }) {
    const [poolQuery, setPoolQuery] = useState("");
    const [menu, setMenu] = useState(null); // { pid, x, y }

    const byId = useMemo(() => {
        const m = {};
        (players || []).forEach((p) => { m[p.id] = p; });
        (pool || []).forEach((p) => { if (!m[p.id]) m[p.id] = p; });
        (extraPool || []).forEach((p) => { if (!m[p.id]) m[p.id] = p; });
        return m;
    }, [players, pool, extraPool]);

    // How often each player has been slotted at each position across all rosters.
    const { data: posUsage = {} } = useQuery({
        queryKey: ["lineup-position-usage"],
        queryFn: () => api.get("/lineup-position-usage").then((r) => r.data),
        staleTime: 60_000,
    });

    // Hover a player chip → position/handedness info popover (team-building aid).
    const handleInfo = (pid) => (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!pid) { setMenu(null); return; }
        setMenu({ pid, x: e.clientX, y: e.clientY });
    };
    const menuPlayer = menu ? byId[menu.pid] : null;
    const menuUsage = menu ? posUsage[menu.pid] : null;

    // Players in even-strength slots (forward lines, D pairs, goalies, reserves)
    // leave the pool. Power play / penalty kill never remove from the pool, so a
    // skater can be duplicated into those units.
    const placed = new Set();
    (draft.forward_lines || []).forEach((l) => (l || []).forEach((s) => s && s.player_id && placed.add(s.player_id)));
    (draft.d_pairs || []).forEach((l) => (l || []).forEach((s) => s && s.player_id && placed.add(s.player_id)));
    (draft.goalies || []).forEach((s) => s && s.player_id && placed.add(s.player_id));
    (draft.reserves || []).forEach((s) => s && s.player_id && placed.add(s.player_id));

    const q = poolQuery.trim().toLowerCase();
    const available = (pool || [])
        .filter((p) => !placed.has(p.id))
        .filter((p) => !q || (p.name || "").toLowerCase().includes(q))
        .sort((a, b) => (posRank(a) - posRank(b)) || (a.name || "").localeCompare(b.name || ""));
    const availableExtra = (extraPool || [])
        .filter((p) => !placed.has(p.id))
        .filter((p) => !q || (p.name || "").toLowerCase().includes(q))
        .sort((a, b) => (posRank(a) - posRank(b)) || (a.name || "").localeCompare(b.name || ""));

    const clearSlotKey = (key) => {
        const d = JSON.parse(JSON.stringify(draft));
        if (keyType(key) === "res") d.reserves = d.reserves.filter((_, i) => i !== +key.split("-")[1]);
        else writeSlot(d, key, { ...EMPTY_SLOT });
        setDraft(d);
    };
    const setReserveLabel = (i, label) => {
        const d = JSON.parse(JSON.stringify(draft));
        d.reserves[i] = { ...d.reserves[i], label };
        setDraft(d);
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        const srcD = source.droppableId;
        const dstD = destination.droppableId;
        if (srcD === dstD) return; // dropped back onto itself (single-occupancy)

        let pid = null, srcKey = null, srcResIdx = -1;
        if (draggableId.startsWith("pool:")) pid = draggableId.slice(5);
        else if (draggableId.startsWith("slot:")) { srcKey = draggableId.slice(5); pid = (getSlot(draft, srcKey) || {}).player_id || null; }
        else if (draggableId.startsWith("res:")) { srcResIdx = +draggableId.slice(4); pid = (draft.reserves[srcResIdx] || {}).player_id || null; }
        if (!pid) return;

        const p = byId[pid] || { name: pid };
        const val = { player_id: pid, player_name: p.name || "" };
        const srcType = srcKey ? keyType(srcKey) : (srcResIdx >= 0 ? "res" : "pool");
        const d = JSON.parse(JSON.stringify(draft));

        // → pool: free the source.
        if (dstD === "pool" || dstD === "pool_extra") {
            if (srcKey) writeSlot(d, srcKey, { ...EMPTY_SLOT });
            else if (srcResIdx >= 0) d.reserves = d.reserves.filter((_, i) => i !== srcResIdx);
            setDraft(d);
            return;
        }
        // → reserves.
        if (dstD === "reserves") {
            if (srcType === "res") return;
            if ((d.reserves || []).some((r) => r.player_id === pid)) return;
            if (srcKey) writeSlot(d, srcKey, { ...EMPTY_SLOT });
            d.reserves = [...(d.reserves || []), { label: "Scratch", position: posTag(p), player_id: pid, player_name: p.name || "" }];
            setDraft(d);
            return;
        }
        // → a slot.
        if (!dstD.startsWith("slot:")) return;
        const tgtKey = dstD.slice(5);
        if (tgtKey === srcKey) return;
        const tgtType = keyType(tgtKey);
        // Duplicate (copy) when dragging an even-strength skater into a PP/PK slot.
        const copy = ["fw", "dp", "g"].includes(srcType) && ST_TYPES.has(tgtType);
        const prev = { ...getSlot(d, tgtKey) };
        writeSlot(d, tgtKey, val);
        if (!copy) {
            if (srcType === "res") {
                d.reserves = d.reserves.filter((r) => r.player_id !== pid);
            } else if (srcKey) {
                // Swap: the displaced occupant moves into the source slot.
                writeSlot(d, srcKey, prev.player_id ? { player_id: prev.player_id, player_name: prev.player_name } : { ...EMPTY_SLOT });
            }
            // From the pool: a displaced occupant simply returns to the pool.
        }
        setDraft(d);
    };

    const fwLine = (i) => (
        <BuilderBlock key={i} label={i === 4 ? "Extra Forwards" : `Line ${i + 1}`}>
            <div className="grid grid-cols-3 gap-1.5" data-testid={`builder-fw-line-${i + 1}`}>
                {draft.forward_lines[i].map((s, j) => (
                    <SlotCell key={j} slotKey={`fw-${i}-${j}`} label={FW_SLOT_LABELS[j]} val={s} byId={byId} onClear={() => clearSlotKey(`fw-${i}-${j}`)} onInfo={handleInfo} />
                ))}
            </div>
        </BuilderBlock>
    );
    const dPair = (i) => (
        <BuilderBlock key={i} label={i === 3 ? "Extra Defenders" : `D-Pair ${i + 1}`}>
            <div className="grid grid-cols-2 gap-1.5" data-testid={`builder-d-pair-${i + 1}`}>
                {draft.d_pairs[i].map((s, j) => (
                    <SlotCell key={j} slotKey={`dp-${i}-${j}`} label={D_SLOT_LABELS[j]} val={s} byId={byId} onClear={() => clearSlotKey(`dp-${i}-${j}`)} onInfo={handleInfo} />
                ))}
            </div>
        </BuilderBlock>
    );
    const stUnit = (unit, labels, cols) => (
        <BuilderBlock key={unit} label={unit.toUpperCase()}>
            <div className={`grid grid-cols-${cols} gap-1.5`} data-testid={`builder-${unit}`}>
                {draft[unit].map((s, j) => (
                    <SlotCell key={j} slotKey={`${unit}-${j}`} label={labels[j]} val={s} byId={byId} onClear={() => clearSlotKey(`${unit}-${j}`)} onInfo={handleInfo} />
                ))}
            </div>
        </BuilderBlock>
    );

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="space-y-4" data-testid="lineup-builder">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                    <BuilderColumn title="Forwards" primary={primary} testId="builder-col-forwards">
                        {draft.forward_lines.map((_, i) => fwLine(i))}
                    </BuilderColumn>
                    <BuilderColumn title="Defence" primary={primary} testId="builder-col-defence">
                        {draft.d_pairs.map((_, i) => dPair(i))}
                    </BuilderColumn>
                    <BuilderColumn title="Goaltenders" primary={primary} testId="builder-col-goalies">
                        <div className="grid grid-cols-2 gap-1.5">
                            {draft.goalies.map((s, j) => (
                                <SlotCell key={j} slotKey={`g-${j}`} label={G_SLOT_LABELS[j]} val={s} byId={byId} onClear={() => clearSlotKey(`g-${j}`)} onInfo={handleInfo} />
                            ))}
                        </div>
                    </BuilderColumn>
                    <BuilderColumn title="Reserves" primary={primary} testId="builder-col-reserves">
                        <ReservesColumn reserves={draft.reserves} byId={byId} onClear={clearSlotKey} onLabelChange={setReserveLabel} reserveLabels={reserveLabels} onInfo={handleInfo} />
                    </BuilderColumn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <BuilderColumn title="Power Play" primary={primary} testId="builder-col-pp">
                        {stUnit("pp1", PP_SLOT_LABELS, 5)}
                        {stUnit("pp2", PP_SLOT_LABELS, 5)}
                    </BuilderColumn>
                    <BuilderColumn title="Penalty Kill" primary={primary} testId="builder-col-pk">
                        {stUnit("pk1", PK_SLOT_LABELS, 4)}
                        {stUnit("pk2", PK_SLOT_LABELS, 4)}
                    </BuilderColumn>
                </div>

                <PoolBox players={available} query={poolQuery} setQuery={setPoolQuery} onInfo={handleInfo} />
                {(extraPool || []).length > 0 && (
                    <PoolBox players={availableExtra} query={poolQuery} onInfo={handleInfo}
                        droppableId="pool_extra" testId="lineup-pool-extra-box"
                        title="Also on Roster · Did Not Dress"
                        emptyHint="No extra roster players available." />
                )}
                <p className="text-[11px] text-[#64748B]">
                    Drag players from the box into positions. Dropping one player onto another <span className="text-[#C9A6F0] font-semibold">swaps</span> them. Players in Forwards/Defence/Goalies/Reserves leave the box; dragging a forward or defender into a Power Play / Penalty Kill slot <span className="text-[#C9A6F0] font-semibold">duplicates</span> them. Drag a placed player back to the box to remove. <span className="text-[#C9A6F0] font-semibold">Hover</span> a player to see their handedness, preferred &amp; most-common position.
                </p>
            </div>

            {menu && menuPlayer && (
                <>
                    <div
                        className="fixed z-[101] bg-[#0A0B10] border border-[#9D4CDD]/50 rounded-lg shadow-xl p-3 w-56 text-xs pointer-events-none"
                        style={{ left: Math.min(menu.x + 12, window.innerWidth - 240), top: Math.min(menu.y, window.innerHeight - 160) }}
                        data-testid="lineup-info-popover"
                    >
                        <p className="font-heading text-sm font-bold text-white mb-2 truncate">{menuPlayer.name}</p>
                        <div className="space-y-1.5">
                            <div className="flex justify-between gap-2">
                                <span className="text-[#64748B] uppercase tracking-wider text-[10px]">Handedness</span>
                                <span className="font-bold text-[#93C5FD]" data-testid="lineup-info-handedness">
                                    {menuPlayer.shoots === "L" ? "Left" : menuPlayer.shoots === "R" ? "Right" : "—"}
                                </span>
                            </div>
                            <div className="flex justify-between gap-2">
                                <span className="text-[#64748B] uppercase tracking-wider text-[10px]">Preferred</span>
                                <span className="font-bold text-[#E9D5FF]" data-testid="lineup-info-preferred">{menuPlayer.pref_position || menuPlayer.position || "—"}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                                <span className="text-[#64748B] uppercase tracking-wider text-[10px]">Most common</span>
                                <span className="font-bold text-[#86EFAC]" data-testid="lineup-info-most-common">
                                    {menuUsage?.most_common || "—"}
                                    {menuUsage?.total ? <span className="text-[#64748B] font-normal ml-1">({menuUsage.total})</span> : null}
                                </span>
                            </div>
                            {menuUsage?.counts && Object.keys(menuUsage.counts).length > 1 && (
                                <div className="pt-1 border-t border-[#1E2233] flex flex-wrap gap-1" data-testid="lineup-info-breakdown">
                                    {Object.entries(menuUsage.counts).sort((a, b) => b[1] - a[1]).map(([pos, n]) => (
                                        <span key={pos} className="pill bg-[#1E2233] text-[#94A3B8] text-[9px]">{pos} {n}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </DragDropContext>
    );
}

export default LineupBuilder;
